import React, { useMemo } from 'react';
import { Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PERIOD_PRESETS } from './managementRange';
import { IITH } from '@/pages/landing/iithBrand';

/** Top filter bar: period · specialisation · doctor (all combinations). */
export default function ManagementFilterBar({
  period, setPeriod, specialisation, setSpecialisation,
  doctorId, setDoctorId, doctors = [], specialisations = [], rangeLabel, onClear,
}) {
  const filteredDoctors = useMemo(() => {
    if (!specialisation) return doctors;
    return doctors.filter((d) => d.specialization === specialisation);
  }, [doctors, specialisation]);

  const active = Boolean(specialisation || doctorId || (period && period !== 'month'));

  return (
    <div
      className="rounded-2xl border bg-white shadow-sm px-4 py-3 sm:px-5"
      data-testid="mgmt-filter-bar"
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Filter className="h-3.5 w-3.5 text-zinc-400" />
        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          Filters
        </p>
        <span className="text-[11px] text-zinc-400">·</span>
        <p className="text-[11px] text-zinc-500">
          Showing <span className="font-semibold text-zinc-700">{rangeLabel}</span>
          {specialisation ? <> · <span className="font-semibold text-zinc-700">{specialisation}</span></> : null}
          {doctorId ? (
            <> · <span className="font-semibold text-zinc-700">
              {filteredDoctors.find((d) => d.id === doctorId)?.name
                || doctors.find((d) => d.id === doctorId)?.name
                || 'Doctor'}
            </span></>
          ) : null}
        </p>
        {active ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto h-7 text-xs text-zinc-600"
            onClick={onClear}
            data-testid="mgmt-filter-clear"
          >
            <X className="h-3.5 w-3.5 mr-1" /> Reset
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          className="h-9 min-w-[150px] border rounded-lg px-2.5 text-xs bg-white"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          data-testid="mgmt-filter-period"
        >
          {PERIOD_PRESETS.map((p) => (
            <option key={p.v} value={p.v}>{p.l}</option>
          ))}
        </select>

        <select
          className="h-9 min-w-[170px] border rounded-lg px-2.5 text-xs bg-white"
          value={specialisation}
          onChange={(e) => {
            setSpecialisation(e.target.value);
            setDoctorId('');
          }}
          data-testid="mgmt-filter-spec"
        >
          <option value="">All specialisations</option>
          {specialisations.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          className="h-9 min-w-[200px] border rounded-lg px-2.5 text-xs bg-white"
          value={doctorId}
          onChange={(e) => setDoctorId(e.target.value)}
          data-testid="mgmt-filter-doctor"
        >
          <option value="">All doctors</option>
          {filteredDoctors.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}{d.specialization ? ` — ${d.specialization}` : ''}
            </option>
          ))}
        </select>

        <span
          className="hidden sm:inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-2.5 rounded-lg"
          style={{ background: IITH.mist, color: IITH.navy }}
        >
          Combinations apply to all tiles
        </span>
      </div>
    </div>
  );
}
